import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ScrollArea } from '@/components/ui/scroll-area';
import Header from '../home/components/Header';
import MacroEnv from './components/MacroEnv';
import ClientNeeds, { ClientNeedsRef } from './components/ClientNeeds';
import AssetAllocation from './components/AssetAllocation';
import { useAllocationData } from './hooks/useAllocationData';
import { SkeletonCard } from '@/components/loading/Skeleton';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { Upload } from 'lucide-react';
import { generateExportContent } from '@/utils/dataFormatter';
import { confirm } from '@/components/confirm';
import dayjs from 'dayjs';
import useUserStore from '@/stores/userStore';

const CustomAssetAllocation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const clientNeedsRef = useRef<ClientNeedsRef>(null);
  const { hasData = false } = (location.state as { hasData: boolean }) || {};

  const {
    loading,
    generatePlanLoading,
    hasData: currentHasData,
    hasFetched,
    fetchInitialData,
    generatePlan,
    initData
  } = useAllocationData(hasData);

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchInitialData();
    }
  }, []);

  const { userInfo } = useUserStore();

  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    if (isExporting) return;

    const ok = await confirm({
      title: '导出',
      description: '是否导出完整报告？'
    });
    if (ok) {
      setIsExporting(true);
      const customAssetAllocationPageEl = document.querySelector(
        '#custom-asset-allocation-page'
      ) as HTMLElement;
      if (customAssetAllocationPageEl) {
        generateExportContent(
          customAssetAllocationPageEl,
          `${userInfo.customer_name}_定制资产配置方案_${dayjs().format('YYYY-MM-DD HH:mm:ss')}.jpg`,
          () => {
            setIsExporting(false);
          }
        );
      } else {
        setIsExporting(false);
      }
    }
  };

  const handleGeneratePlan = (text: string) => {
    if (generatePlanLoading) {
      toast.info('方案生成中，请稍后再试');
      return;
    }
    generatePlan(text);
  };

  const handleRefresh = () => {
    // clientNeedsRef.current?.handleGeneratePlan();
    if (loading) {
      toast.info('数据加载中，请稍后再试');
      return;
    }
    if (generatePlanLoading) {
      toast.info('方案生成中，请稍后再试');
      return;
    }
    initData();
  };

  const goBack = () => navigate(-1);

  return (
    <div className="relative mx-auto flex h-screen flex-col overflow-hidden pb-6">
      <Header goBack={goBack} refresh={handleRefresh}>
        <div className="text-lg font-semibold leading-none tracking-tight">定制资产配置方案</div>
      </Header>
      {/* {(loading || generatePlanLoading) && (
        <div className="absolute bottom-0 top-14 z-[11111] w-full bg-white">
          {Array.from({ length: 10 }).map((_, index) => (
            <SkeletonCard key={index} />
          ))}
        </div>
      )} */}

      <ScrollArea className="scroll-area-block h-full w-full">
        <div className="mb-10 space-y-6 px-4" id="custom-asset-allocation-page">
          {loading ? (
            <SkeletonCard />
          ) : (
            <>
              {/* 宏观市场环境分析 */}
              <MacroEnv />
              {/* 客户投资需求 */}
              <ClientNeeds
                ref={clientNeedsRef}
                onGeneratePlan={handleGeneratePlan}
                generatePlanLoading={generatePlanLoading}
                hasData={currentHasData}
              />
              {/* 资产配置方案 */}
              {currentHasData && <AssetAllocation />}
            </>
          )}
        </div>
      </ScrollArea>
      {/* 底部操作栏 */}
      <div className="absolute bottom-0 left-0 right-0 z-[11] rounded-lg px-4">
        <div className="flex items-center justify-end bg-white p-3">
          <Button
            className="h-6 rounded border border-[#5959EB] px-2 py-1 text-sm text-[#5959EB] disabled:opacity-50"
            disabled={isExporting}
            variant="outline"
            onClick={handleExport}
          >
            {isExporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            <span>{isExporting ? '导出中...' : '导出'}</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CustomAssetAllocation;
